"use client";

import * as React from "react";

export interface FloatingDropdownPosition {
  top: number;
  left: number;
  width: number;
}

export interface UseFloatingDropdownArgs {
  enabled: boolean;
  open: boolean;
  triggerRef: React.RefObject<HTMLButtonElement>;
  containerRef: React.RefObject<HTMLDivElement>;
  dropdownRef: React.RefObject<HTMLDivElement>;
  setOpen: (open: boolean) => void;
}

/**
 * Encapsulates the floating dropdown side-effects used by the
 * expandInline=floating variant of ResponsiveCommandMultiSelect:
 *  - measure trigger to position fixed dropdown
 *  - close on outside pointerdown
 *  - close on Escape (capture phase)
 *
 * Behavior preserved 1:1 with the original inline effects.
 */
export function useFloatingDropdown({
  enabled,
  open,
  triggerRef,
  containerRef,
  dropdownRef,
  setOpen,
}: UseFloatingDropdownArgs): FloatingDropdownPosition | null {
  const [dropdownPosition, setDropdownPosition] =
    React.useState<FloatingDropdownPosition | null>(null);

  // Position floating dropdown with fixed coords (escape cell overflow)
  React.useEffect(() => {
    if (!enabled || !open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 280),
    });
    return () => setDropdownPosition(null);
  }, [enabled, open, triggerRef]);

  // Close on outside click
  React.useEffect(() => {
    if (!enabled || !open) return;
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [enabled, open, setOpen, containerRef, dropdownRef]);

  // Close on Escape key
  React.useEffect(() => {
    if (!enabled || !open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handleKeyDown, true);
    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [enabled, open, setOpen, triggerRef]);

  return dropdownPosition;
}
